import 'typeface-roboto';
import 'bz-utilities/global';

import React from 'react';
import { Route, BrowserRouter as Router } from 'react-router-dom';
import { StripeProvider } from 'react-stripe-elements';

import { computed, observable, runInAction } from 'mobx';
import { Provider, observer } from 'mobx-react';

import MomentUtils from '@date-io/moment';

import MobileDetect from 'mobile-detect';

import { CircularProgress, MuiThemeProvider, createTheme } from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';

import ClearAcceptProvider from 'bz-core-controls/controls/payments/clear-accept/provider';
import BzTheme from 'bz-core-controls/styles/theme';

import { Register } from 'bz-icons';

import MediatorStore from 'bz-utilities/modules/plugins/register/stores/mediator';

import MitelChat from 'connect/core/controls/chat/mitel';
import ConnectFeature from 'connect/core/controls/features-flags/feature';
import connectFeaturesStore from 'connect/core/controls/features-flags/feature-store';

import { cache as coreCache } from 'tm3-core/cache';
import FeatureEnum from 'tm3-core/enums/feature-enum';

import ModuleFeatureProvider from 'tm3-core/controls/module/provider';
import cache from './cache';
import Manifest from './manifest';
import SquareWrapper from './modules/square-provider-wrapper';
import AuthStore, { AuthStoreContext } from './stores/auth-store';
import ProviderStore, { ProviderStoreContext } from './stores/provider-store';
import SearchStore, { SearchStoreCtx } from './stores/search-store';
import themes from './themes';

const SiteHidden = React.lazy(() => import('./modules/site-hidden'));
const Shell = React.lazy(() => import('./shell'));

interface IProps {}

@observer
class App extends React.Component {
  @observable isLoaded = false;
  @observable isProvider = false;

  mediator: MediatorStore;

  constructor(props: IProps) {
    super(props);

    cache.providerStore = new ProviderStore();
    cache.authStore = new AuthStore();
    cache.searchStore = new SearchStore();
    connectFeaturesStore.load();
    cache.searchStore.loadingPromise = cache.searchStore.load();

    this.mediator = new MediatorStore();
    coreCache.mediator = this.mediator;

    window.mobileDetection = new MobileDetect(window.navigator.userAgent);

    this.loadConnect();
  }

  render() {
    if (!this.isLoaded) {
      return (
        <div style={{ display: 'grid', margin: '25px', justifyItems: 'center' }}>
          <CircularProgress />
        </div>
      );
    }

    const { isSiteLive, connectEnabled } = cache.providerStore;
    const { stripeKey } = this;

    let content;
    let manifest;
    if (!connectEnabled || !isSiteLive) {
      content = <SiteHidden />;
    } else {
      manifest = <Manifest />;
      content = <Route component={Shell} />;
    }

    return (
      <StripeProvider stripe={stripeKey}>
        <MuiThemeProvider theme={this.theme}>
          <MuiPickersUtilsProvider utils={MomentUtils}>
            <SearchStoreCtx.Provider value={cache.searchStore}>
              <Provider
                authStore={cache.authStore}
                providerStore={cache.providerStore}
                searchStore={cache.searchStore}
                mediator={this.mediator}
              >
                <AuthStoreContext.Provider value={cache.authStore}>
                  <ModuleFeatureProvider authenticated={true}>
                    <ProviderStoreContext.Provider value={cache.providerStore}>
                      <>
                        <ConnectFeature flag={FeatureEnum.FF_VHG_MITEL_CHAT}>
                          <MitelChat />
                        </ConnectFeature>
                        <SquareWrapper>
                          <ClearAcceptProvider>
                            <Register>
                              <React.Fragment>
                                <BzTheme />

                                {manifest}
                                <Router>
                                  <React.Suspense
                                    fallback={
                                      <div>
                                        <CircularProgress />
                                      </div>
                                    }
                                  >
                                    {content}
                                  </React.Suspense>
                                </Router>
                              </React.Fragment>
                            </Register>
                          </ClearAcceptProvider>
                        </SquareWrapper>
                      </>
                    </ProviderStoreContext.Provider>
                  </ModuleFeatureProvider>
                </AuthStoreContext.Provider>
              </Provider>
            </SearchStoreCtx.Provider>
          </MuiPickersUtilsProvider>
        </MuiThemeProvider>
      </StripeProvider>
    );
  }

  private async loadConnect() {
    const params = window.location.search.split('?');
    const embedded = params.includes('embedded');
    try {
      await cache.providerStore.onLoad(embedded);
      runInAction(() => {
        this.isLoaded = true;
        this.isProvider = true;
        cache.authStore.checkUser();
      });
    } catch (err) {
      this.isLoaded = true;
    }
  }

  @computed
  get theme() {
    const { skin = 'default' } = cache.providerStore;
    const theme = themes[skin];

    theme.overrides = {
      MuiFormControl: {
        root: {
          // marginRight: '15px',
          flexGrow: '2'
        }
      },
      MuiTab: {
        root: {
          fontWeight: 700,
          '@media(min-width: 960px)': {
            minWidth: '5px'
          }
        }
      },

      MuiButton: {
        root: {
          fontWeight: 600
        }
      },
      MuiAppBar: {
        root: {
          boxShadow: 'none'
        }
      }
    };

    theme.typography = {
      fontFamily: ['Nunito Sans', 'sans-serif'],
      htmlFontSize: 14,
      fontSize: 13,
      useNextVariants: true
    };

    theme.primary = cache.providerStore.customColourPrimary || theme['primary'];
    theme.secondary = cache.providerStore.customColourSecondary || theme['secondary'];
    theme.nav = cache.providerStore.customColourNav || theme['nav'];
    theme.navText = cache.providerStore.customColourNavText || theme['navText'];

    theme.palette = {
      // using '#fff' instead of 'white' prevents date pickers from crashing in register form
      primary: { main: theme.primary || themes['default'].primary },
      secondary: { main: theme.secondary || themes['default'].secondary },
      nav: {
        main: theme.nav || themes['default'].nav,
        contrastText: theme.navText || themes['default'].navText
      },
      error: { main: '#dc3545' },
      tonalOffset: 0.2
    };

    return createTheme(theme);
  }

  @computed
  get stripeKey() {
    const { stripeApiKey, stripeAccount } = cache.providerStore;
    let res = null;
    try {
      if (!!stripeApiKey && window.Stripe) {
        res = window.Stripe(stripeApiKey, { stripeAccount });
      }
    } finally {
      return res;
    }
  }
}

export default App;
